import React, { useState, useEffect, useRef } from "react";
import { api, API_URL, getAuthHeaders } from "../lib/utils";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { Mail, CreditCard, Palette, Shield, Save, Eye, Check, Star, Loader2, Building2, Upload, Trash2, X, Image, Package, Bell, Plus, Info, Server, ChevronDown, ChevronUp, Sparkles, ExternalLink, Percent, FileText, Home, Inbox } from "lucide-react";

// Branding Settings Tab
function BrandingSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [settings, setSettings] = useState({
    company_name: "",
    primary_color: "#000080",
    secondary_color: "#808080",
    accent_color: "#008080",
    tagline: "",
    address: "",
    phone: "",
    email: "",
    website: "",
    logo_url: null
  });
  const fileInputRef = useRef(null);

  useEffect(function() {
    fetchSettings();
  }, []);

  function fetchSettings() {
    api.get("/settings/branding").then(function(data) {
      setSettings({
        company_name: data.company_name || "",
        primary_color: data.primary_color || "#000080",
        secondary_color: data.secondary_color || "#808080",
        accent_color: data.accent_color || "#008080",
        tagline: data.tagline || "",
        address: data.address || "",
        phone: data.phone || "",
        email: data.email || "",
        website: data.website || "",
        logo_url: data.logo_url || null
      });
    }).catch(function(err) {
      console.error(err);
    }).finally(function() {
      setLoading(false);
    });
  }

  function handleSave() {
    if (!settings.company_name) {
      toast.error("Company name is required");
      return;
    }
    setSaving(true);
    api.post("/settings/branding", settings).then(function() {
      toast.success("Branding settings saved");
    }).catch(function(err) {
      toast.error(err.message || "Failed to save");
    }).finally(function() {
      setSaving(false);
    });
  }

  function handleLogoUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Logo must be less than 10MB");
      return;
    }

    setUploadingLogo(true);
    const formData = new FormData();
    formData.append("file", file);

    fetch(API_URL + "/settings/branding/logo", {
      method: "POST",
      headers: getAuthHeaders(),
      body: formData
    }).then(function(res) {
      if (!res.ok) throw new Error("Upload failed");
      return res.json();
    }).then(function(data) {
      setSettings({ ...settings, logo_url: data.logo_url });
      toast.success("Logo uploaded");
    }).catch(function(err) {
      toast.error(err.message);
    }).finally(function() {
      setUploadingLogo(false);
    });
  }

  function handleDeleteLogo() {
    api.delete("/settings/branding/logo").then(function() {
      setSettings({ ...settings, logo_url: null });
      toast.success("Logo deleted");
    }).catch(function(err) {
      toast.error(err.message);
    });
  }

  if (loading) {
    return <div className="flex items-center justify-center p-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-6">
      <Card className="rounded-3xl bg-card/50 backdrop-blur-xl border-white/10">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            Company Logo
          </CardTitle>
          <CardDescription>Upload your company logo to appear on invoices and quotes</CardDescription>
        </CardHeader>
        <CardContent>
          <input type="file" ref={fileInputRef} onChange={handleLogoUpload} accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml" className="hidden" data-testid="logo-upload-input" />
          
          <div className="flex items-center gap-6">
            <div className="w-32 h-32 rounded-2xl border-2 border-dashed border-border flex items-center justify-center bg-accent/20 overflow-hidden">
              {settings.logo_url ? (
                <img src={API_URL + settings.logo_url} alt="Company Logo" className="w-full h-full object-contain" />
              ) : (
                <Image className="w-10 h-10 text-muted-foreground" />
              )}
            </div>
            <div className="space-y-3">
              <Button onClick={function() { fileInputRef.current?.click(); }} disabled={uploadingLogo} variant="outline" className="rounded-full" data-testid="upload-logo-btn">
                {uploadingLogo ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                Upload Logo
              </Button>
              {settings.logo_url && (
                <Button onClick={handleDeleteLogo} variant="ghost" className="rounded-full text-destructive" data-testid="delete-logo-btn">
                  <Trash2 className="w-4 h-4 mr-2" /> Remove
                </Button>
              )}
              <p className="text-xs text-muted-foreground">Max 10MB. PNG, JPG, SVG, or GIF</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-3xl bg-card/50 backdrop-blur-xl border-white/10">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            Company Information
          </CardTitle>
          <CardDescription>Your company details for invoices and quotes</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Company Name *</Label>
              <Input value={settings.company_name} onChange={function(e) { setSettings({ ...settings, company_name: e.target.value }); }} className="rounded-xl" placeholder="Your Company" data-testid="company-name" />
            </div>
            <div className="space-y-2">
              <Label>Tagline</Label>
              <Input value={settings.tagline} onChange={function(e) { setSettings({ ...settings, tagline: e.target.value }); }} className="rounded-xl" placeholder="Your business tagline" data-testid="company-tagline" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={settings.email} onChange={function(e) { setSettings({ ...settings, email: e.target.value }); }} className="rounded-xl" placeholder="contact@company.com" data-testid="company-email" />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input value={settings.phone} onChange={function(e) { setSettings({ ...settings, phone: e.target.value }); }} className="rounded-xl" placeholder="+1 (555) 000-0000" data-testid="company-phone" />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Address</Label>
            <Textarea value={settings.address} onChange={function(e) { setSettings({ ...settings, address: e.target.value }); }} className="rounded-xl" placeholder="123 Business St, City, State 12345" data-testid="company-address" />
          </div>

          <div className="space-y-2">
            <Label>Website</Label>
            <Input value={settings.website} onChange={function(e) { setSettings({ ...settings, website: e.target.value }); }} className="rounded-xl" placeholder="https://yourcompany.com" data-testid="company-website" />
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-3xl bg-card/50 backdrop-blur-xl border-white/10">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Palette className="w-5 h-5 text-primary" />
            Brand Colors
          </CardTitle>
          <CardDescription>Customize colors used on invoices and quotes</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="space-y-3">
              <Label>Primary Color</Label>
              <div className="flex items-center gap-3">
                <input type="color" value={settings.primary_color} onChange={function(e) { setSettings({ ...settings, primary_color: e.target.value }); }} className="w-12 h-12 rounded-xl cursor-pointer border-0" data-testid="primary-color" />
                <Input value={settings.primary_color} onChange={function(e) { setSettings({ ...settings, primary_color: e.target.value }); }} className="rounded-xl font-mono uppercase" />
              </div>
              <p className="text-xs text-muted-foreground">Headers, buttons, accents</p>
            </div>

            <div className="space-y-3">
              <Label>Secondary Color</Label>
              <div className="flex items-center gap-3">
                <input type="color" value={settings.secondary_color} onChange={function(e) { setSettings({ ...settings, secondary_color: e.target.value }); }} className="w-12 h-12 rounded-xl cursor-pointer border-0" data-testid="secondary-color" />
                <Input value={settings.secondary_color} onChange={function(e) { setSettings({ ...settings, secondary_color: e.target.value }); }} className="rounded-xl font-mono uppercase" />
              </div>
              <p className="text-xs text-muted-foreground">Highlights, badges</p>
            </div>

            <div className="space-y-3">
              <Label>Accent Color</Label>
              <div className="flex items-center gap-3">
                <input type="color" value={settings.accent_color} onChange={function(e) { setSettings({ ...settings, accent_color: e.target.value }); }} className="w-12 h-12 rounded-xl cursor-pointer border-0" data-testid="accent-color" />
                <Input value={settings.accent_color} onChange={function(e) { setSettings({ ...settings, accent_color: e.target.value }); }} className="rounded-xl font-mono uppercase" />
              </div>
              <p className="text-xs text-muted-foreground">Success states, totals</p>
            </div>
          </div>

          <div className="mt-6 p-4 rounded-xl bg-accent/30">
            <p className="text-sm font-medium mb-3">Preview</p>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="px-4 py-2 rounded-full text-white text-sm font-medium" style={{ backgroundColor: settings.primary_color }}>Primary Button</div>
              <div className="px-4 py-2 rounded-full text-white text-sm font-medium" style={{ backgroundColor: settings.secondary_color }}>Secondary Badge</div>
              <div className="px-4 py-2 rounded-full text-white text-sm font-medium" style={{ backgroundColor: settings.accent_color }}>$1,234.56</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} className="rounded-full shadow-glow-cyan" data-testid="save-branding">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save Branding
        </Button>
      </div>
    </div>
  );
}

// Email Settings Tab
function EmailSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [emailMethod, setEmailMethod] = useState("gmail");
  const [gmailSettings, setGmailSettings] = useState({ gmail_address: "", app_password: "", from_name: "" });
  const [smtpSettings, setSmtpSettings] = useState({ host: "", port: 587, username: "", password: "", from_email: "", from_name: "", use_tls: true });
  const [configured, setConfigured] = useState(false);
  const [configuredMethod, setConfiguredMethod] = useState(null);
  const [showSetupGuide, setShowSetupGuide] = useState(false);

  useEffect(function() {
    api.get("/settings/smtp").then(function(data) {
      if (data.configured) {
        setConfigured(true);
        var method = data.email_method || "smtp";
        setEmailMethod(method);
        setConfiguredMethod(method);
        if (method === "gmail") {
          setGmailSettings({ gmail_address: data.username || "", app_password: "", from_name: data.from_name || "" });
        } else {
          setSmtpSettings({ host: data.host || "", port: data.port || 587, username: data.username || "", password: "", from_email: data.from_email || "", from_name: data.from_name || "", use_tls: data.use_tls !== false });
        }
      }
    }).catch(function(err) { console.error(err); }).finally(function() { setLoading(false); });
  }, []);

  function handleSave() {
    if (emailMethod === "gmail") {
      if (!gmailSettings.gmail_address || (!gmailSettings.app_password && !configured)) { toast.error("Gmail address and app password are required"); return; }
      setSaving(true);
      api.post("/settings/smtp", { method: "gmail", gmail: gmailSettings }).then(function() { toast.success("Gmail email settings saved"); setConfigured(true); setConfiguredMethod("gmail"); }).catch(function(err) { toast.error(err.message || "Failed to save"); }).finally(function() { setSaving(false); });
    } else {
      if (!smtpSettings.host || !smtpSettings.username || (!smtpSettings.password && !configured) || !smtpSettings.from_email) { toast.error("Please fill required fields"); return; }
      setSaving(true);
      api.post("/settings/smtp", { method: "smtp", smtp: smtpSettings }).then(function() { toast.success("SMTP email settings saved"); setConfigured(true); setConfiguredMethod("smtp"); }).catch(function(err) { toast.error(err.message || "Failed to save"); }).finally(function() { setSaving(false); });
    }
  }

  if (loading) return <div className="flex items-center justify-center p-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <Card className="rounded-3xl bg-card/50 backdrop-blur-xl border-white/10">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="font-heading flex items-center gap-2"><Mail className="w-5 h-5 text-primary" />Email Configuration</CardTitle>
            <CardDescription className="mt-1">Configure email sending for invoices and quotes</CardDescription>
          </div>
          {configured && <Badge><Check className="w-3 h-3 mr-1" /> {configuredMethod === "gmail" ? "Gmail" : "SMTP"} Configured</Badge>}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Method Selector */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button type="button" onClick={function() { setEmailMethod("gmail"); }} className={"relative flex flex-col items-center gap-2 p-6 transition-all " + (emailMethod === "gmail" ? "win-well" : "win-raised")}>
            {emailMethod === "gmail" && <div className="absolute top-3 right-3"><Check className="w-4 h-4 text-primary" /></div>}
            <Mail className={"w-8 h-8 " + (emailMethod === "gmail" ? "text-primary" : "text-muted-foreground")} />
            <span className="font-semibold text-lg">Gmail</span>
            <span className="text-sm text-muted-foreground">Quick setup with App Password</span>
          </button>
          <button type="button" onClick={function() { setEmailMethod("smtp"); }} className={"relative flex flex-col items-center gap-2 p-6 transition-all " + (emailMethod === "smtp" ? "win-well" : "win-raised")}>
            {emailMethod === "smtp" && <div className="absolute top-3 right-3"><Check className="w-4 h-4 text-primary" /></div>}
            <Server className={"w-8 h-8 " + (emailMethod === "smtp" ? "text-primary" : "text-muted-foreground")} />
            <span className="font-semibold text-lg">Custom SMTP</span>
            <span className="text-sm text-muted-foreground">Any SMTP server</span>
          </button>
        </div>

        {configured && configuredMethod && configuredMethod !== emailMethod && (
          <div className="flex items-start gap-3 p-4 win-well">
            <Info className="w-5 h-5 text-primary shrink-0 mt-0.5" />
            <p className="text-sm text-foreground">Saving will replace your current <strong>{configuredMethod === "gmail" ? "Gmail" : "SMTP"}</strong> configuration. Only one email method can be active at a time.</p>
          </div>
        )}

        {/* Gmail Form */}
        {emailMethod === "gmail" && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Gmail Address *</Label><Input type="email" value={gmailSettings.gmail_address} onChange={function(e) { setGmailSettings({ ...gmailSettings, gmail_address: e.target.value }); }} placeholder="you@gmail.com" className="rounded-xl" /></div>
              <div className="space-y-2"><Label>App Password *</Label><Input type="password" value={gmailSettings.app_password} onChange={function(e) { setGmailSettings({ ...gmailSettings, app_password: e.target.value }); }} placeholder="xxxx xxxx xxxx xxxx" className="rounded-xl" />{configured && configuredMethod === "gmail" && <p className="text-xs text-muted-foreground">Leave blank to keep existing</p>}</div>
            </div>
            <div className="space-y-2"><Label>From Name</Label><Input value={gmailSettings.from_name} onChange={function(e) { setGmailSettings({ ...gmailSettings, from_name: e.target.value }); }} placeholder="Your Business Name" className="rounded-xl" /></div>

            {/* Setup Guide */}
            <div className="rounded-xl border border-white/10 overflow-hidden">
              <button type="button" onClick={function() { setShowSetupGuide(!showSetupGuide); }} className="flex items-center justify-between w-full p-4 text-left hover:bg-accent/10 transition-colors">
                <div className="flex items-center gap-2"><Info className="w-4 h-4 text-primary" /><span className="font-medium text-sm">How to create a Gmail App Password</span></div>
                {showSetupGuide ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
              </button>
              {showSetupGuide && (
                <div className="px-4 pb-4 space-y-3 text-sm text-muted-foreground border-t border-white/10 pt-3">
                  <p className="font-medium text-foreground">You must have 2-Step Verification enabled on your Google account.</p>
                  <ol className="list-decimal list-inside space-y-2">
                    <li>Go to <strong>myaccount.google.com</strong></li>
                    <li>Navigate to <strong>Security</strong> &rarr; <strong>2-Step Verification</strong></li>
                    <li>Scroll to the bottom and click <strong>App passwords</strong></li>
                    <li>Enter a name (e.g. &ldquo;KyberBusiness&rdquo;) and click <strong>Create</strong></li>
                    <li>Copy the 16-character password and paste it above</li>
                  </ol>
                  <p className="text-xs">You only see the password once, but you can always generate a new one. If you don&rsquo;t see the App Passwords option, enable 2-Step Verification first.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* SMTP Form */}
        {emailMethod === "smtp" && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>SMTP Host *</Label><Input value={smtpSettings.host} onChange={function(e) { setSmtpSettings({ ...smtpSettings, host: e.target.value }); }} placeholder="smtp.example.com" className="rounded-xl" data-testid="smtp-host" /></div>
              <div className="space-y-2"><Label>Port *</Label><Input type="number" value={smtpSettings.port} onChange={function(e) { setSmtpSettings({ ...smtpSettings, port: parseInt(e.target.value) || 587 }); }} className="rounded-xl" data-testid="smtp-port" /></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Username *</Label><Input value={smtpSettings.username} onChange={function(e) { setSmtpSettings({ ...smtpSettings, username: e.target.value }); }} className="rounded-xl" data-testid="smtp-username" /></div>
              <div className="space-y-2"><Label>Password *</Label><Input type="password" value={smtpSettings.password} onChange={function(e) { setSmtpSettings({ ...smtpSettings, password: e.target.value }); }} className="rounded-xl" data-testid="smtp-password" />{configured && configuredMethod === "smtp" && <p className="text-xs text-muted-foreground">Leave blank to keep existing</p>}</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>From Email *</Label><Input type="email" value={smtpSettings.from_email} onChange={function(e) { setSmtpSettings({ ...smtpSettings, from_email: e.target.value }); }} className="rounded-xl" data-testid="smtp-from-email" /></div>
              <div className="space-y-2"><Label>From Name</Label><Input value={smtpSettings.from_name} onChange={function(e) { setSmtpSettings({ ...smtpSettings, from_name: e.target.value }); }} className="rounded-xl" data-testid="smtp-from-name" /></div>
            </div>
            <div className="flex items-center justify-between p-4 rounded-xl bg-accent/30">
              <div><p className="font-medium">Use TLS</p><p className="text-sm text-muted-foreground">Secure connection</p></div>
              <Switch checked={smtpSettings.use_tls} onCheckedChange={function(c) { setSmtpSettings({ ...smtpSettings, use_tls: c }); }} />
            </div>
          </div>
        )}

        <div className="flex justify-end"><Button onClick={handleSave} disabled={saving} className="rounded-full shadow-glow-cyan" data-testid="save-smtp">{saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Save</Button></div>
      </CardContent>
    </Card>
  );
}

// PayPal Settings Tab
function PayPalSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({ client_id: "", client_secret: "", sandbox: true });
  const [configured, setConfigured] = useState(false);

  useEffect(function() {
    api.get("/settings/paypal").then(function(data) {
      if (data.configured) { setSettings({ client_id: "", client_secret: "", sandbox: data.sandbox !== false }); setConfigured(true); }
    }).catch(function(err) { console.error(err); }).finally(function() { setLoading(false); });
  }, []);

  function handleSave() {
    if (!settings.client_id || !settings.client_secret) { toast.error("Please fill required fields"); return; }
    setSaving(true);
    api.post("/settings/paypal", settings).then(function() { toast.success("PayPal saved"); setConfigured(true); }).catch(function(err) { toast.error(err.message); }).finally(function() { setSaving(false); });
  }

  if (loading) return <div className="flex items-center justify-center p-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <Card className="rounded-3xl bg-card/50 backdrop-blur-xl border-white/10">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="font-heading flex items-center gap-2"><CreditCard className="w-5 h-5 text-primary" />PayPal Configuration</CardTitle>
            <CardDescription className="mt-1">Connect PayPal for payments</CardDescription>
          </div>
          {configured && <Badge><Check className="w-3 h-3 mr-1" /> Configured</Badge>}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="p-4 rounded-xl bg-secondary/10 border border-secondary/20">
          <p className="text-sm text-muted-foreground">Get credentials from <a href="https://developer.paypal.com/dashboard/applications/live" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">PayPal Developer Dashboard</a></p>
        </div>
        <div className="space-y-2"><Label>Client ID *</Label><Input value={settings.client_id} onChange={function(e) { setSettings({ ...settings, client_id: e.target.value }); }} className="rounded-xl font-mono text-sm" data-testid="paypal-client-id" /></div>
        <div className="space-y-2"><Label>Client Secret *</Label><Input type="password" value={settings.client_secret} onChange={function(e) { setSettings({ ...settings, client_secret: e.target.value }); }} className="rounded-xl" data-testid="paypal-client-secret" />{configured && <p className="text-xs text-muted-foreground">Leave blank to keep existing</p>}</div>
        <div className="flex items-center justify-between p-4 rounded-xl bg-accent/30">
          <div><p className="font-medium">Sandbox Mode</p><p className="text-sm text-muted-foreground">Use for testing</p></div>
          <Switch checked={settings.sandbox} onCheckedChange={function(c) { setSettings({ ...settings, sandbox: c }); }} />
        </div>
        <div className="flex justify-end"><Button onClick={handleSave} disabled={saving} className="rounded-full shadow-glow-magenta bg-secondary" data-testid="save-paypal">{saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Save</Button></div>
      </CardContent>
    </Card>
  );
}

// Gemini / AI Settings Tab
function GeminiSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [current, setCurrent] = useState({ has_api_key: false, api_key_masked: "", model: "gemini-3.5-flash-lite", monthly_cap_usd: 15.0, over_cap_behavior: "warn" });
  const [apiKeyInput, setApiKeyInput] = useState("");
  const [modelChoice, setModelChoice] = useState("gemini-3.5-flash-lite");
  const [capUsd, setCapUsd] = useState(15);
  const [behavior, setBehavior] = useState("warn");
  const [usage, setUsage] = useState(null);

  useEffect(function() {
    api.get("/settings/gemini").then(function(data) {
      setCurrent(data);
      setModelChoice(data.model || "gemini-3.5-flash-lite");
      setCapUsd(typeof data.monthly_cap_usd === "number" ? data.monthly_cap_usd : 8);
      setBehavior(data.over_cap_behavior || "warn");
    }).catch(function(err) { console.error(err); }).finally(function() { setLoading(false); });
    api.get("/settings/gemini/usage").then(function(u) { setUsage(u); }).catch(function() {});
  }, []);

  function handleSave() {
    if (capUsd < 0) { toast.error("Cap must be >= 0"); return; }
    setSaving(true);
    api.put("/settings/gemini", {
      api_key: apiKeyInput,  // empty = keep existing
      model: modelChoice,
      monthly_cap_usd: parseFloat(capUsd),
      over_cap_behavior: behavior,
    }).then(function() {
      toast.success("Gemini settings saved");
      setApiKeyInput("");
      return api.get("/settings/gemini");
    }).then(function(data) { if (data) setCurrent(data); })
    .catch(function(err) { toast.error(err.message || "Save failed"); })
    .finally(function() { setSaving(false); });
  }

  function handleDeleteKey() {
    if (!window.confirm("Clear the Gemini API key? Scans will stop working until a new one is saved.")) return;
    api.delete("/settings/gemini/api-key").then(function() {
      toast.success("API key cleared");
      setCurrent({ ...current, has_api_key: false, api_key_masked: "" });
    }).catch(function(err) { toast.error(err.message); });
  }

  if (loading) return <div className="flex items-center justify-center p-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <Card className="rounded-3xl bg-card/50 backdrop-blur-xl border-white/10">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="font-heading flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" />AI / Gemini</CardTitle>
            <CardDescription className="mt-1">Powers the receipt scanner in Expenses</CardDescription>
          </div>
          {current.has_api_key && <Badge><Check className="w-3 h-3 mr-1" /> Configured</Badge>}
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="p-4 rounded-xl bg-primary/10 border border-primary/20 space-y-2">
          <p className="text-sm text-muted-foreground">Get a key from <a href="https://aistudio.google.com/apikey" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">Google AI Studio<ExternalLink className="w-3 h-3" /></a></p>
          <p className="text-sm text-muted-foreground">At Gemini 2.5 Flash rates, one receipt scan costs ~$0.001 (≈1,000 scans per $1).</p>
        </div>

        <div className="space-y-2">
          <Label>API Key {current.has_api_key && <span className="text-xs text-muted-foreground">(current: {current.api_key_masked})</span>}</Label>
          <Input
            type="password"
            value={apiKeyInput}
            onChange={function(e) { setApiKeyInput(e.target.value); }}
            placeholder={current.has_api_key ? "Leave blank to keep existing" : "Paste your Gemini API key"}
            className="rounded-xl font-mono text-sm"
            data-testid="gemini-api-key"
          />
          {current.has_api_key && (
            <Button variant="outline" size="sm" onClick={handleDeleteKey} className="rounded-full text-destructive"><Trash2 className="w-3 h-3 mr-1" />Clear key</Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Model</Label>
            <Select value={modelChoice} onValueChange={setModelChoice}>
              <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-xl">
                <SelectItem value="gemini-3.5-flash-lite">Gemini 3.5 Flash-Lite (recommended, ~$0.001/scan)</SelectItem>
                <SelectItem value="gemini-3.1-flash-lite">Gemini 3.1 Flash-Lite (cheaper output)</SelectItem>
                <SelectItem value="gemini-2.5-flash">Gemini 2.5 Flash (retires 2026-10-16)</SelectItem>
                <SelectItem value="gemini-2.5-pro">Gemini 2.5 Pro (retires 2026-10-16)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Monthly Spend Cap (USD)</Label>
            <Input type="number" min="0" step="1" value={capUsd} onChange={function(e) { setCapUsd(e.target.value); }} className="rounded-xl" data-testid="gemini-cap" />
            <p className="text-xs text-muted-foreground">Mirrors the hard cap at <a href="https://aistudio.google.com/spend" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">aistudio.google.com/spend</a> — that ceiling is shared by every app on the API key, so this is not a separate allowance.</p>
          </div>
        </div>

        <div className="space-y-2">
          <Label>When cap is exceeded</Label>
          <Select value={behavior} onValueChange={setBehavior}>
            <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-xl">
              <SelectItem value="warn">Warn in UI but keep scanning</SelectItem>
              <SelectItem value="block">Block scans until next month</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {usage && (
          <div className="p-4 rounded-xl bg-accent/30 border border-white/5 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium">This month ({usage.month})</span>
              <span className="text-muted-foreground">{usage.scan_count} scans</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span>${usage.cost_usd.toFixed(4)} / ${usage.cap_usd.toFixed(2)}</span>
              <span className="text-muted-foreground">{usage.percent_used.toFixed(1)}% used</span>
            </div>
            <div className="h-2 rounded-full bg-background overflow-hidden">
              <div
                className={`h-full transition-all ${usage.percent_used >= 100 ? "bg-destructive" : usage.percent_used >= 75 ? "bg-amber-500" : "bg-primary"}`}
                style={{ width: `${Math.min(usage.percent_used, 100)}%` }}
              />
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} className="rounded-full shadow-glow-cyan" data-testid="save-gemini">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Billing Inbox (billing@ -> Gmail label -> Gemini -> expenses)
// Self-expiring "NEW" marker for the tab; drop after the date passes.
const BILLING_INBOX_NEW_UNTIL = new Date("2026-10-26T00:00:00");

function BillingInboxSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [running, setRunning] = useState(false);
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({
    enabled: false,
    imap_host: "imap.gmail.com",
    imap_port: 993,
    username: "",
    label: "invoices",
    poll_minutes: 15,
    auto_post_min_confidence: "high",
    lookback_days: 30,
    notify_reviews: true,
    not_before: "",
  });
  const [domainsText, setDomainsText] = useState("");
  const [passwordInput, setPasswordInput] = useState("");
  // Second mailbox, polled in the same run (Gmail + Fastmail side by side during the move).
  const [sec, setSec] = useState({ enabled: false, imap_host: "imap.fastmail.com", imap_port: 993, username: "", label: "" });
  const [secPasswordInput, setSecPasswordInput] = useState("");

  function load() {
    return api.get("/settings/email-ingest").then(function(data) {
      setStatus(data);
      setForm({
        enabled: !!data.enabled,
        imap_host: data.imap_host || "imap.gmail.com",
        imap_port: data.imap_port || 993,
        username: data.username || "",
        label: data.label || "invoices",
        poll_minutes: data.poll_minutes || 15,
        auto_post_min_confidence: data.auto_post_min_confidence || "high",
        lookback_days: data.lookback_days || 30,
        notify_reviews: data.notify_reviews !== false,
        not_before: data.not_before || "",
      });
      const s = data.secondary || {};
      setSec({
        enabled: !!s.enabled,
        imap_host: s.imap_host || "imap.fastmail.com",
        imap_port: s.imap_port || 993,
        username: s.username || "",
        label: s.label || "",
      });
      setDomainsText((data.trusted_sender_domains || []).join("\n"));
    }).catch(function(err) { console.error(err); });
  }

  function setSecField(key, value) {
    setSec(function(s) { return { ...s, [key]: value }; });
  }

  useEffect(function() {
    load().finally(function() { setLoading(false); });
  }, []);

  function set(key, value) {
    setForm(function(f) { return { ...f, [key]: value }; });
  }

  function handleSave() {
    setSaving(true);
    const payload = {
      ...form,
      imap_port: parseInt(form.imap_port, 10) || 993,
      poll_minutes: parseInt(form.poll_minutes, 10) || 15,
      lookback_days: parseInt(form.lookback_days, 10) || 30,
      trusted_sender_domains: domainsText.split(/[\s,]+/).filter(Boolean),
      password: passwordInput || null,
      not_before: form.not_before || null,
      secondary: {
        ...sec,
        imap_port: parseInt(sec.imap_port, 10) || 993,
        password: secPasswordInput || null,
      },
    };
    api.put("/settings/email-ingest", payload)
      .then(function(data) {
        setStatus(data);
        setPasswordInput("");
        setSecPasswordInput("");
        setDomainsText((data.trusted_sender_domains || []).join("\n"));
        toast.success("Billing inbox settings saved");
      })
      .catch(function(err) { toast.error(err.message || "Save failed"); })
      .finally(function() { setSaving(false); });
  }

  function handleTest() {
    setTesting(true);
    api.post("/settings/email-ingest/test", {})
      .then(function(data) { toast.success(data.message); })
      .catch(function(err) { toast.error(err.message || "Connection failed"); })
      .finally(function() { setTesting(false); });
  }

  function handleRun() {
    setRunning(true);
    api.post("/settings/email-ingest/run", {})
      .then(function(data) {
        toast.success(data.message);
        // Each email takes a few seconds in Gemini; refresh the status a little later.
        setTimeout(function() { load(); }, 20000);
      })
      .catch(function(err) { toast.error(err.message || "Could not start the import"); })
      .finally(function() { setRunning(false); });
  }

  function when(iso) {
    return iso ? new Date(iso).toLocaleString() : "never";
  }

  if (loading) return <div className="flex items-center justify-center p-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const stats = (status && status.stats) || {};

  return (
    <Card className="rounded-3xl bg-card/50 backdrop-blur-xl border-white/10">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="font-heading flex items-center gap-2"><Inbox className="w-5 h-5 text-primary" />Billing Inbox</CardTitle>
            <CardDescription className="mt-1">Imports invoices and receipts emailed to billing@ as expenses, using the Gemini key from the AI tab</CardDescription>
          </div>
          {status && status.enabled && <Badge><Check className="w-3 h-3 mr-1" /> On</Badge>}
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="p-4 rounded-xl bg-primary/10 border border-primary/20 space-y-2 text-sm text-muted-foreground">
          <p>Mail is read over IMAP from up to two mailboxes and is never deleted. A message in both is imported once. Imported mail gets a kb-posted, kb-review, kb-skipped or kb-failed label (a keyword on non-Gmail servers).</p>
          <p>An expense posts on its own only when the scan is confident, complete, and from a trusted sender. Everything else waits under <span className="font-medium">Needs review</span> on the Expenses page and stays out of reports until approved. Approving one trusts that sender from then on.</p>
        </div>

        <div className="flex items-center justify-between p-4 rounded-xl bg-accent/30 border border-white/5">
          <div>
            <Label>Import automatically</Label>
            <p className="text-xs text-muted-foreground mt-1">Checks every {form.poll_minutes} minutes</p>
          </div>
          <Switch checked={form.enabled} onCheckedChange={function(v) { set("enabled", v); }} data-testid="billing-inbox-enabled" />
        </div>

        <div className="flex items-center justify-between p-4 rounded-xl bg-accent/30 border border-white/5">
          <div>
            <Label>Send review items to Discord</Label>
            <p className="text-xs text-muted-foreground mt-1">Posts a preview to #kyber-business through NotiFi. React ✅ to approve or ❌ to reject, or open the title to fix a field first.</p>
          </div>
          <Switch checked={form.notify_reviews} onCheckedChange={function(v) { set("notify_reviews", v); }} data-testid="billing-inbox-notify-reviews" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Mailbox 1 server</Label>
            <Input value={form.imap_host} onChange={function(e) { set("imap_host", e.target.value); }} placeholder="imap.gmail.com" className="rounded-xl font-mono text-sm" data-testid="billing-inbox-host" />
          </div>
          <div className="space-y-2">
            <Label>Login</Label>
            <Input value={form.username} onChange={function(e) { set("username", e.target.value); }} placeholder="starforgesales@gmail.com" className="rounded-xl" data-testid="billing-inbox-username" />
          </div>
          <div className="space-y-2">
            <Label>App password {status && status.has_password && <span className="text-xs text-muted-foreground">(saved)</span>}</Label>
            <Input type="password" value={passwordInput} onChange={function(e) { setPasswordInput(e.target.value); }} placeholder={status && status.has_password ? "Leave blank to keep existing" : "App password, not the account password"} className="rounded-xl font-mono text-sm" data-testid="billing-inbox-password" />
          </div>
          <div className="space-y-2">
            <Label>Gmail label or folder</Label>
            <Input value={form.label} onChange={function(e) { set("label", e.target.value); }} className="rounded-xl" data-testid="billing-inbox-label" />
          </div>
          <div className="space-y-2">
            <Label>Auto-post when the scan is</Label>
            <Select value={form.auto_post_min_confidence} onValueChange={function(v) { set("auto_post_min_confidence", v); }}>
              <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-xl">
                <SelectItem value="high">High confidence (recommended)</SelectItem>
                <SelectItem value="medium">Medium confidence or better</SelectItem>
                <SelectItem value="never">Never: review everything</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Check every (minutes)</Label>
            <Input type="number" min="5" value={form.poll_minutes} onChange={function(e) { set("poll_minutes", e.target.value); }} className="rounded-xl" />
          </div>
          <div className="space-y-2">
            <Label>Look back (days)</Label>
            <Input type="number" min="1" max="365" value={form.lookback_days} onChange={function(e) { set("lookback_days", e.target.value); }} className="rounded-xl" />
          </div>
          <div className="space-y-2">
            <Label>Ignore mail received before</Label>
            <Input type="date" value={form.not_before} onChange={function(e) { set("not_before", e.target.value); }} className="rounded-xl" data-testid="billing-inbox-not-before" />
            <p className="text-xs text-muted-foreground">Applies to both mailboxes. Keeps bulk-imported old receipts from becoming new expenses.</p>
          </div>
        </div>

        <div className="p-4 rounded-xl bg-accent/30 border border-white/5 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label>Second mailbox</Label>
              <p className="text-xs text-muted-foreground mt-1">Checked in the same run, e.g. Fastmail alongside Gmail. Trust, confidence and dates are shared.</p>
            </div>
            <Switch checked={sec.enabled} onCheckedChange={function(v) { setSecField("enabled", v); }} data-testid="billing-inbox-sec-enabled" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Server</Label>
              <Input value={sec.imap_host} onChange={function(e) { setSecField("imap_host", e.target.value); }} placeholder="imap.fastmail.com" className="rounded-xl font-mono text-sm" data-testid="billing-inbox-sec-host" />
            </div>
            <div className="space-y-2">
              <Label>Login</Label>
              <Input value={sec.username} onChange={function(e) { setSecField("username", e.target.value); }} className="rounded-xl" data-testid="billing-inbox-sec-username" />
            </div>
            <div className="space-y-2">
              <Label>App password {status && status.secondary && status.secondary.has_password && <span className="text-xs text-muted-foreground">(saved)</span>}</Label>
              <Input type="password" value={secPasswordInput} onChange={function(e) { setSecPasswordInput(e.target.value); }} placeholder={status && status.secondary && status.secondary.has_password ? "Leave blank to keep existing" : "App password, not the account password"} className="rounded-xl font-mono text-sm" data-testid="billing-inbox-sec-password" />
            </div>
            <div className="space-y-2">
              <Label>Folder</Label>
              <Input value={sec.label} onChange={function(e) { setSecField("label", e.target.value); }} placeholder="The Star Forge/Billing" className="rounded-xl" data-testid="billing-inbox-sec-label" />
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Trusted sender domains</Label>
          <Textarea value={domainsText} onChange={function(e) { setDomainsText(e.target.value); }} placeholder="one per line, e.g. amazon.com" className="rounded-xl font-mono text-sm min-h-[90px]" data-testid="billing-inbox-domains" />
          <p className="text-xs text-muted-foreground">Filled in automatically when you approve an imported expense. Remove a domain to send its mail back to review.</p>
        </div>

        {status && (
          <div className="p-4 rounded-xl bg-accent/30 border border-white/5 space-y-1 text-sm">
            <div className="flex justify-between"><span>Last check</span><span className="text-muted-foreground">{when(status.last_poll_at)}</span></div>
            <div className="flex justify-between"><span>Last successful check</span><span className="text-muted-foreground">{when(status.last_success_at)}</span></div>
            {status.last_error && <div className="text-destructive">Last error: {status.last_error}</div>}
            <div className="flex justify-between pt-1">
              <span>Imported</span>
              <span className="text-muted-foreground">
                {(stats.auto_posted || 0)} posted · {(stats.needs_review || 0)} to review · {(stats.skipped || 0)} skipped · {(stats.duplicate || 0)} duplicate · {(stats.failed || 0)} failed
              </span>
            </div>
            {status.needs_review > 0 && <div className="text-amber-500">{status.needs_review} expense(s) waiting for review</div>}
          </div>
        )}

        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" onClick={handleTest} disabled={testing} className="rounded-full" data-testid="billing-inbox-test">
            {testing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Server className="w-4 h-4 mr-2" />}
            Test connection
          </Button>
          <Button variant="outline" onClick={handleRun} disabled={running} className="rounded-full" data-testid="billing-inbox-run">
            {running ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Inbox className="w-4 h-4 mr-2" />}
            Import now
          </Button>
          <Button onClick={handleSave} disabled={saving} className="rounded-full shadow-glow-cyan" data-testid="billing-inbox-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Tax Settings Tab
function TaxSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [percentInput, setPercentInput] = useState("7.8");

  useEffect(function() {
    api.get("/settings/tax").then(function(data) {
      const rate = typeof data.rate === "number" ? data.rate : 0.078;
      setPercentInput((rate * 100).toFixed(2).replace(/\.?0+$/, ""));
    }).catch(function(err) { console.error(err); }).finally(function() { setLoading(false); });
  }, []);

  function handleSave() {
    const pct = parseFloat(percentInput);
    if (isNaN(pct) || pct < 0 || pct > 100) {
      toast.error("Tax rate must be between 0 and 100");
      return;
    }
    setSaving(true);
    api.put("/settings/tax", { rate: pct / 100 })
      .then(function() { toast.success("Tax rate saved"); })
      .catch(function(err) { toast.error(err.message || "Save failed"); })
      .finally(function() { setSaving(false); });
  }

  if (loading) return <div className="flex items-center justify-center p-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <Card className="rounded-3xl bg-card/50 backdrop-blur-xl border-white/10">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2"><Percent className="w-5 h-5 text-primary" />Tax Rate</CardTitle>
        <CardDescription className="mt-1">Applied to all new invoices and quotes. The rate at creation is saved on each document, so historical records keep their original rate.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="space-y-2 max-w-xs">
          <Label>Rate (%)</Label>
          <div className="flex gap-2 items-center">
            <Input
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={percentInput}
              onChange={function(e) { setPercentInput(e.target.value); }}
              className="rounded-xl font-mono"
              data-testid="tax-rate-input"
            />
            <span className="text-muted-foreground">%</span>
          </div>
          <p className="text-xs text-muted-foreground">Example: <span className="font-mono">7.8</span> = 7.8% sales tax</p>
        </div>

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} className="rounded-full shadow-glow-cyan">
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            <Save className="w-4 h-4 mr-2" />Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Rent + Utilities Settings Tab
function RentUtilitiesSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({ default_rent_amount: 0, utility_percentage: 25, internet_amount: 25, phone_amount: 10 });

  useEffect(function() {
    api.get("/settings/rent-utilities").then(function(data) {
      setSettings({
        default_rent_amount: data.default_rent_amount ?? 0,
        utility_percentage: data.utility_percentage ?? 25,
        internet_amount: data.internet_amount ?? 25,
        phone_amount: data.phone_amount ?? 10,
      });
    }).catch(function(err) { console.error(err); }).finally(function() { setLoading(false); });
  }, []);

  function handleSave() {
    setSaving(true);
    api.put("/settings/rent-utilities", settings)
      .then(function() { toast.success("Rent + Utilities settings saved"); })
      .catch(function(err) { toast.error(err.message || "Save failed"); })
      .finally(function() { setSaving(false); });
  }

  if (loading) return <div className="flex items-center justify-center p-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <Card className="rounded-3xl bg-card/50 backdrop-blur-xl border-white/10">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2"><Home className="w-5 h-5 text-primary" />Rent + Utilities</CardTitle>
        <CardDescription className="mt-1">Defaults used by "Record Monthly Expenses"</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2"><Label>Default Rent Write-off Amount ($)</Label><Input type="number" step="0.01" value={settings.default_rent_amount} onChange={function(e) { setSettings({ ...settings, default_rent_amount: parseFloat(e.target.value) || 0 }); }} className="rounded-xl" data-testid="rent-default-amount" /></div>
          <div className="space-y-2"><Label>Utility Expense Percentage (%)</Label><Input type="number" step="0.1" value={settings.utility_percentage} onChange={function(e) { setSettings({ ...settings, utility_percentage: parseFloat(e.target.value) || 0 }); }} className="rounded-xl" data-testid="rent-utility-percentage" /></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2"><Label>Monthly Internet Amount ($)</Label><Input type="number" step="0.01" value={settings.internet_amount} onChange={function(e) { setSettings({ ...settings, internet_amount: parseFloat(e.target.value) || 0 }); }} className="rounded-xl" data-testid="rent-internet-amount" /></div>
          <div className="space-y-2"><Label>Monthly Phone Amount ($)</Label><Input type="number" step="0.01" value={settings.phone_amount} onChange={function(e) { setSettings({ ...settings, phone_amount: parseFloat(e.target.value) || 0 }); }} className="rounded-xl" data-testid="rent-phone-amount" /></div>
        </div>
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} className="rounded-full shadow-glow-cyan" data-testid="save-rent-utilities">
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            <Save className="w-4 h-4 mr-2" />Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Terms & Conditions Tab — one card per document type (invoice / quote).
function TermsSettings() {
  return (
    <div className="space-y-6">
      <TermsCard docType="invoice" title="Invoice Terms" description="Default block prefilled on every new invoice. Snapshotted at creation, so changes only affect future invoices. Each invoice can be edited individually." />
      <TermsCard docType="quote" title="Quote Terms" description="Default block prefilled on every new quote. Snapshotted at creation, so changes only affect future quotes. Each quote can be edited individually." />
    </div>
  );
}

function TermsCard(props) {
  const { docType, title, description } = props;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState("");

  useEffect(function() {
    api.get("/settings/terms/" + docType).then(function(d) {
      setContent(typeof d.content === "string" ? d.content : "");
    }).catch(function(err) { console.error(err); }).finally(function() { setLoading(false); });
  }, [docType]);

  function handleSave() {
    setSaving(true);
    api.put("/settings/terms/" + docType, { content: content })
      .then(function() { toast.success(title + " saved"); })
      .catch(function(err) { toast.error(err.message || "Save failed"); })
      .finally(function() { setSaving(false); });
  }

  if (loading) return <div className="flex items-center justify-center p-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <Card className="rounded-3xl bg-card/50 backdrop-blur-xl border-white/10">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2"><FileText className="w-5 h-5 text-primary" />{title}</CardTitle>
        <CardDescription className="mt-1">{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="space-y-2">
          <Label>Default text</Label>
          <Textarea
            rows={10}
            value={content}
            onChange={function(e) { setContent(e.target.value); }}
            placeholder="e.g. Payment due within 30 days. Late payments accrue 1.5% interest per month..."
            className="rounded-xl font-mono text-sm"
            data-testid={"terms-content-input-" + docType}
          />
          <p className="text-xs text-muted-foreground">Plain text, up to 10,000 characters. Newlines are preserved on PDFs and on the View page.</p>
        </div>

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} className="rounded-full shadow-glow-cyan">
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            <Save className="w-4 h-4 mr-2" />Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Email Templates Tab
function EmailTemplates() {
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(function() {
    api.get("/email-templates").then(function(data) { setTemplates(data); }).catch(function() { toast.error("Failed to load templates"); }).finally(function() { setLoading(false); });
  }, []);

  function handleSetDefault(templateId) {
    api.post("/email-templates/" + templateId + "/set-default").then(function() { toast.success("Default updated"); api.get("/email-templates").then(setTemplates); }).catch(function(err) { toast.error(err.message); });
  }

  function handleEditSave() {
    if (!editData) return;
    setSaving(true);
    api.put("/email-templates/" + editData.id, { name: editData.name, theme: editData.theme, subject: editData.subject, body_html: editData.body_html })
      .then(function() { toast.success("Template updated"); setEditOpen(false); api.get("/email-templates").then(setTemplates); })
      .catch(function(err) { toast.error(err.message); }).finally(function() { setSaving(false); });
  }

  // Win98: template theme renders as a plain inset .win-well chip (the theme
  // name text carries the meaning); no colored pill.
  function getThemeColor() {
    return "";
  }

  if (loading) return <div className="flex items-center justify-center p-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const templateCards = [];
  for (let i = 0; i < templates.length; i++) {
    const t = templates[i];
    templateCards.push(
      <div key={t.id} className={"p-4 rounded-2xl border transition-all " + (t.is_default ? "border-primary bg-primary/5" : "border-border hover:border-primary/50")}>
        <div className="flex items-start justify-between mb-3">
          <div><h3 className="font-medium">{t.name}</h3><Badge className={getThemeColor(t.theme) + " capitalize mt-1"}>{t.theme}</Badge></div>
          {t.is_default && <Badge className="bg-primary/20 text-primary"><Star className="w-3 h-3 mr-1" /> Default</Badge>}
        </div>
        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{t.subject}</p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={function() { setSelectedTemplate(t); setPreviewOpen(true); }} className="rounded-full flex-1"><Eye className="w-4 h-4 mr-1" /> Preview</Button>
          <Button variant="outline" size="sm" onClick={function() { setEditData({ ...t }); setEditOpen(true); }} className="rounded-full">Edit</Button>
          {!t.is_default && <Button variant="ghost" size="sm" onClick={function() { handleSetDefault(t.id); }} className="rounded-full"><Star className="w-4 h-4" /></Button>}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="rounded-3xl bg-card/50 backdrop-blur-xl border-white/10">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2"><Palette className="w-5 h-5 text-primary" />Email Templates</CardTitle>
          <CardDescription>Customize email templates for invoices</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">{templateCards}</div>
        </CardContent>
      </Card>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl rounded-3xl">
          <DialogHeader><DialogTitle>Template Preview: {selectedTemplate?.name}</DialogTitle></DialogHeader>
          <div className="mt-4 p-4 rounded-xl bg-white text-black" dangerouslySetInnerHTML={{ __html: selectedTemplate?.body_html || "" }} />
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-3xl rounded-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Template</DialogTitle></DialogHeader>
          {editData && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Template Name</Label><Input value={editData.name} onChange={function(e) { setEditData({ ...editData, name: e.target.value }); }} className="rounded-xl" /></div>
                <div className="space-y-2">
                  <Label>Theme</Label>
                  <Select value={editData.theme} onValueChange={function(v) { setEditData({ ...editData, theme: v }); }}>
                    <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-xl"><SelectItem value="professional">Professional</SelectItem><SelectItem value="modern">Modern</SelectItem><SelectItem value="minimal">Minimal</SelectItem><SelectItem value="bold">Bold</SelectItem><SelectItem value="classic">Classic</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2"><Label>Subject Line</Label><Input value={editData.subject} onChange={function(e) { setEditData({ ...editData, subject: e.target.value }); }} className="rounded-xl" /><p className="text-xs text-muted-foreground">Variables: {"{invoice_number}"}, {"{total}"}, {"{due_date}"}, {"{payment_link}"}</p></div>
              <div className="space-y-2"><Label>HTML Body</Label><Textarea value={editData.body_html} onChange={function(e) { setEditData({ ...editData, body_html: e.target.value }); }} className="rounded-xl font-mono text-sm min-h-[300px]" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={function() { setEditOpen(false); }} className="rounded-full">Cancel</Button>
            <Button onClick={handleEditSave} disabled={saving} className="rounded-full shadow-glow-cyan">{saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Order Sources Settings Tab
function OrderSourcesSettings() {
  const [loading, setLoading] = useState(true);
  const [sources, setSources] = useState([]);
  const [newSource, setNewSource] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(function() {
    fetchSources();
  }, []);

  function fetchSources() {
    api.get("/config/order-sources").then(function(data) {
      setSources(data);
    }).catch(function() {
      toast.error("Failed to load sources");
    }).finally(function() {
      setLoading(false);
    });
  }

  function handleAdd() {
    if (!newSource.trim()) { toast.error("Enter a source name"); return; }
    setSaving(true);
    api.post("/config/order-sources", { name: newSource.trim() }).then(function() {
      toast.success("Source added");
      setNewSource("");
      fetchSources();
    }).catch(function(err) {
      toast.error(err.message || "Failed to add source");
    }).finally(function() {
      setSaving(false);
    });
  }

  function handleDelete(id) {
    api.delete("/config/order-sources/" + id).then(function() {
      toast.success("Source deleted");
      fetchSources();
    }).catch(function(err) {
      toast.error(err.message || "Failed to delete source");
    });
  }

  if (loading) return <div className="flex items-center justify-center p-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <Card className="rounded-3xl bg-card/50 backdrop-blur-xl border-white/10">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2"><Package className="w-5 h-5 text-primary" />Order Sources</CardTitle>
        <CardDescription>Configure where orders come from (e.g., Etsy, Website, IRL)</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Input
            value={newSource}
            onChange={function(e) { setNewSource(e.target.value); }}
            placeholder="New source name..."
            className="rounded-xl"
            onKeyDown={function(e) { if (e.key === "Enter") handleAdd(); }}
          />
          <Button onClick={handleAdd} disabled={saving} className="rounded-full">
            <Plus className="w-4 h-4 mr-2" />Add
          </Button>
        </div>
        <div className="space-y-2">
          {sources.map(function(source) {
            return (
              <div key={source.id} className="flex items-center justify-between p-3 rounded-xl bg-accent/20 border border-border/40">
                <span className="font-medium">{source.name}</span>
                <Button variant="ghost" size="icon" onClick={function() { handleDelete(source.id); }} className="h-8 w-8 text-muted-foreground hover:text-destructive">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            );
          })}
          {sources.length === 0 && <p className="text-center text-muted-foreground py-4">No sources configured</p>}
        </div>
      </CardContent>
    </Card>
  );
}

// Order Statuses Settings Tab
function OrderStatusesSettings() {
  const [loading, setLoading] = useState(true);
  const [statuses, setStatuses] = useState([]);
  const [newStatus, setNewStatus] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(function() {
    fetchStatuses();
  }, []);

  function fetchStatuses() {
    api.get("/config/order-statuses").then(function(data) {
      setStatuses(data);
    }).catch(function() {
      toast.error("Failed to load statuses");
    }).finally(function() {
      setLoading(false);
    });
  }

  function handleAdd() {
    if (!newStatus.trim()) { toast.error("Enter a status name"); return; }
    setSaving(true);
    api.post("/config/order-statuses", { name: newStatus.trim() }).then(function() {
      toast.success("Status added");
      setNewStatus("");
      fetchStatuses();
    }).catch(function(err) {
      toast.error(err.message || "Failed to add status");
    }).finally(function() {
      setSaving(false);
    });
  }

  function handleDelete(id) {
    api.delete("/config/order-statuses/" + id).then(function() {
      toast.success("Status deleted");
      fetchStatuses();
    }).catch(function(err) {
      toast.error(err.message || "Failed to delete status");
    });
  }

  if (loading) return <div className="flex items-center justify-center p-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <Card className="rounded-3xl bg-card/50 backdrop-blur-xl border-white/10">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2"><Package className="w-5 h-5 text-primary" />Order Statuses</CardTitle>
        <CardDescription>Configure the workflow statuses for order tracking (e.g., Waiting, Printing, Shipped)</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Input
            value={newStatus}
            onChange={function(e) { setNewStatus(e.target.value); }}
            placeholder="New status name..."
            className="rounded-xl"
            onKeyDown={function(e) { if (e.key === "Enter") handleAdd(); }}
          />
          <Button onClick={handleAdd} disabled={saving} className="rounded-full">
            <Plus className="w-4 h-4 mr-2" />Add
          </Button>
        </div>
        <div className="space-y-2">
          {statuses.map(function(status, index) {
            return (
              <div key={status.id} className="flex items-center justify-between p-3 rounded-xl bg-accent/20 border border-border/40">
                <div className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground font-mono w-6">{index + 1}</span>
                  <span className="font-medium">{status.name}</span>
                </div>
                <Button variant="ghost" size="icon" onClick={function() { handleDelete(status.id); }} className="h-8 w-8 text-muted-foreground hover:text-destructive">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            );
          })}
          {statuses.length === 0 && <p className="text-center text-muted-foreground py-4">No statuses configured</p>}
        </div>
        <p className="text-xs text-muted-foreground">Statuses are displayed in the order shown above. &quot;Complete&quot; and &quot;Shipped&quot; are treated as terminal statuses.</p>
      </CardContent>
    </Card>
  );
}

// Order Reminders Settings Tab
function OrderRemindersSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({ enabled: false, days_before: 3, email_to: "" });

  useEffect(function() {
    api.get("/settings/reminders").then(function(data) {
      setSettings({ enabled: data.enabled || false, days_before: data.days_before || 3, email_to: data.email_to || "" });
    }).catch(function() {}).finally(function() { setLoading(false); });
  }, []);

  function handleSave() {
    if (settings.enabled && !settings.email_to) { toast.error("Please enter an email address for reminders"); return; }
    setSaving(true);
    api.put("/settings/reminders", settings).then(function() {
      toast.success("Reminder settings saved");
    }).catch(function(err) {
      toast.error(err.message || "Failed to save");
    }).finally(function() {
      setSaving(false);
    });
  }

  if (loading) return <div className="flex items-center justify-center p-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <Card className="rounded-3xl bg-card/50 backdrop-blur-xl border-white/10">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2"><Bell className="w-5 h-5 text-primary" />Order Reminders</CardTitle>
        <CardDescription>Get email reminders for orders approaching their due date. Uses your configured email settings.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between p-4 rounded-xl bg-accent/30">
          <div>
            <p className="font-medium">Enable Reminders</p>
            <p className="text-sm text-muted-foreground">Send daily email at 8:00 AM for upcoming orders</p>
          </div>
          <Switch checked={settings.enabled} onCheckedChange={function(c) { setSettings({ ...settings, enabled: c }); }} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Days Before Due Date</Label>
            <Input
              type="number"
              min="1"
              max="30"
              value={settings.days_before}
              onChange={function(e) { setSettings({ ...settings, days_before: parseInt(e.target.value) || 3 }); }}
              className="rounded-xl"
            />
            <p className="text-xs text-muted-foreground">Remind about orders due within this many days</p>
          </div>
          <div className="space-y-2">
            <Label>Send Reminders To</Label>
            <Input
              type="email"
              value={settings.email_to}
              onChange={function(e) { setSettings({ ...settings, email_to: e.target.value }); }}
              placeholder="you@example.com"
              className="rounded-xl"
            />
          </div>
        </div>
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} className="rounded-full shadow-glow-cyan">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Main Settings Page
export function SettingsPage() {
  const { isAdmin } = useAuth();

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-bold font-heading">Settings</h1>
        <p className="text-muted-foreground mt-1">Configure your KyberBusiness application</p>
      </div>

      <Tabs defaultValue="branding" className="space-y-6">
        <TabsList className="win-raised p-1 flex-wrap h-auto gap-1">
          <TabsTrigger value="branding" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Building2 className="w-4 h-4 mr-2" />Branding
          </TabsTrigger>
          <TabsTrigger value="smtp" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Mail className="w-4 h-4 mr-2" />Email
          </TabsTrigger>
          <TabsTrigger value="paypal" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <CreditCard className="w-4 h-4 mr-2" />PayPal
          </TabsTrigger>
          <TabsTrigger value="tax" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Percent className="w-4 h-4 mr-2" />Tax
          </TabsTrigger>
          <TabsTrigger value="terms" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <FileText className="w-4 h-4 mr-2" />Terms
          </TabsTrigger>
          <TabsTrigger value="templates" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Palette className="w-4 h-4 mr-2" />Templates
          </TabsTrigger>
          <TabsTrigger value="order-sources" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Package className="w-4 h-4 mr-2" />Sources
          </TabsTrigger>
          <TabsTrigger value="order-statuses" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Package className="w-4 h-4 mr-2" />Statuses
          </TabsTrigger>
          <TabsTrigger value="reminders" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Bell className="w-4 h-4 mr-2" />Reminders
          </TabsTrigger>
          <TabsTrigger value="gemini" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Sparkles className="w-4 h-4 mr-2" />AI
          </TabsTrigger>
          <TabsTrigger value="billing-inbox" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-billing-inbox">
            <Inbox className="w-4 h-4 mr-2" />Billing Inbox
            {new Date() < BILLING_INBOX_NEW_UNTIL && <span className="ml-2 px-1.5 py-0.5 rounded-full bg-secondary text-[10px] font-bold leading-none">NEW</span>}
          </TabsTrigger>
          <TabsTrigger value="rent-utilities" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Home className="w-4 h-4 mr-2" />Rent + Utilities
          </TabsTrigger>
        </TabsList>

        <TabsContent value="branding">
          {isAdmin ? <BrandingSettings /> : (
            <Card className="rounded-3xl bg-card/50 backdrop-blur-xl border-white/10">
              <CardContent className="p-12 text-center"><Shield className="w-12 h-12 mx-auto text-muted-foreground mb-4" /><p className="text-muted-foreground">Admin access required</p></CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="smtp">
          {isAdmin ? <EmailSettings /> : (
            <Card className="rounded-3xl bg-card/50 backdrop-blur-xl border-white/10">
              <CardContent className="p-12 text-center"><Shield className="w-12 h-12 mx-auto text-muted-foreground mb-4" /><p className="text-muted-foreground">Admin access required</p></CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="paypal">
          {isAdmin ? <PayPalSettings /> : (
            <Card className="rounded-3xl bg-card/50 backdrop-blur-xl border-white/10">
              <CardContent className="p-12 text-center"><Shield className="w-12 h-12 mx-auto text-muted-foreground mb-4" /><p className="text-muted-foreground">Admin access required</p></CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="tax">
          {isAdmin ? <TaxSettings /> : (
            <Card className="rounded-3xl bg-card/50 backdrop-blur-xl border-white/10">
              <CardContent className="p-12 text-center"><Shield className="w-12 h-12 mx-auto text-muted-foreground mb-4" /><p className="text-muted-foreground">Admin access required</p></CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="terms">
          {isAdmin ? <TermsSettings /> : (
            <Card className="rounded-3xl bg-card/50 backdrop-blur-xl border-white/10">
              <CardContent className="p-12 text-center"><Shield className="w-12 h-12 mx-auto text-muted-foreground mb-4" /><p className="text-muted-foreground">Admin access required</p></CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="templates"><EmailTemplates /></TabsContent>

        <TabsContent value="order-sources">
          {isAdmin ? <OrderSourcesSettings /> : (
            <Card className="rounded-3xl bg-card/50 backdrop-blur-xl border-white/10">
              <CardContent className="p-12 text-center"><Shield className="w-12 h-12 mx-auto text-muted-foreground mb-4" /><p className="text-muted-foreground">Admin access required</p></CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="order-statuses">
          {isAdmin ? <OrderStatusesSettings /> : (
            <Card className="rounded-3xl bg-card/50 backdrop-blur-xl border-white/10">
              <CardContent className="p-12 text-center"><Shield className="w-12 h-12 mx-auto text-muted-foreground mb-4" /><p className="text-muted-foreground">Admin access required</p></CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="reminders">
          {isAdmin ? <OrderRemindersSettings /> : (
            <Card className="rounded-3xl bg-card/50 backdrop-blur-xl border-white/10">
              <CardContent className="p-12 text-center"><Shield className="w-12 h-12 mx-auto text-muted-foreground mb-4" /><p className="text-muted-foreground">Admin access required</p></CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="gemini">
          {isAdmin ? <GeminiSettings /> : (
            <Card className="rounded-3xl bg-card/50 backdrop-blur-xl border-white/10">
              <CardContent className="p-12 text-center"><Shield className="w-12 h-12 mx-auto text-muted-foreground mb-4" /><p className="text-muted-foreground">Admin access required</p></CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="billing-inbox">
          {isAdmin ? <BillingInboxSettings /> : (
            <Card className="rounded-3xl bg-card/50 backdrop-blur-xl border-white/10">
              <CardContent className="p-12 text-center"><Shield className="w-12 h-12 mx-auto text-muted-foreground mb-4" /><p className="text-muted-foreground">Admin access required</p></CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="rent-utilities">
          {isAdmin ? <RentUtilitiesSettings /> : (
            <Card className="rounded-3xl bg-card/50 backdrop-blur-xl border-white/10">
              <CardContent className="p-12 text-center"><Shield className="w-12 h-12 mx-auto text-muted-foreground mb-4" /><p className="text-muted-foreground">Admin access required</p></CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
